import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UsageMetricTitleKey } from '../../../../Constants/VOSS/usageMetricKey';
import { getRemaining, getUsagePercent, getUsageStatus } from '../../../../Utils/usageUtils';
import { formatPeriod, getInitialPeriodIndex } from '../../../../Utils/usagePeriodUtils';
import { UsageStatus } from '../../../../Constants/VOSS/usageTypes';
import type { CustomerUsageMetric } from '../../../../Constants/VOSS/usageTypes';
import PeriodNavigator from './PeriodNavigator';
import StatusBadge from './StatusBadge';
import UsageMetricBody from './UsageMetricBody';
import { tileStyle } from './usageStatusStyles';

const noop = () => undefined;

interface UsageMetricCardProps {
    metric: CustomerUsageMetric;
}

const UsageMetricCard = ({ metric }: UsageMetricCardProps) => {
    const { t } = useTranslation();
    const [selectedIndex, setSelectedIndex] = useState<number>(() => getInitialPeriodIndex(metric));

    const titleKey = UsageMetricTitleKey[metric.metricKey];
    const title = titleKey !== undefined ? t(titleKey) : (metric.productName ?? '');
    const periods = metric.periods;

    const hasPeriods = periods.length > 0;
    const currentPeriod = hasPeriods ? (periods[selectedIndex] ?? periods[periods.length - 1]) : null;
    const status = currentPeriod ? getUsageStatus(currentPeriod, metric.metricKey) : UsageStatus.Neutral;

    return (
        <div className="d-flex flex-column h-100 rounded-8 border p-16 gap-16">
            <div className="d-flex align-items-center justify-content-between gap-8">
                <h4 className="m-0">{title}</h4>
                {currentPeriod && (
                    <StatusBadge
                        status={status}
                        percent={getUsagePercent(currentPeriod)}
                        metricKey={metric.metricKey}
                        t={t}
                    />
                )}
            </div>
            <div className={hasPeriods ? '' : 'invisible'} aria-hidden={!hasPeriods}>
                <PeriodNavigator
                    label={currentPeriod ? formatPeriod(currentPeriod, t) : ''}
                    onPrev={hasPeriods ? () => setSelectedIndex(i => Math.max(0, i - 1)) : noop}
                    onNext={hasPeriods ? () => setSelectedIndex(i => Math.min(periods.length - 1, i + 1)) : noop}
                    atOldest={selectedIndex <= 0}
                    atNewest={selectedIndex >= periods.length - 1}
                />
            </div>
            <div
                className={`d-flex flex-column align-items-center text-center p-16 gap-12 rounded-8 flex-grow-1 justify-content-${hasPeriods ? 'between' : 'center'}`}
                style={tileStyle(status)}
            >
                {currentPeriod ? (
                    <UsageMetricBody
                        period={currentPeriod}
                        metricKey={metric.metricKey}
                        status={status}
                        remaining={getRemaining(currentPeriod)}
                        t={t}
                    />
                ) : (
                    <p className="m-0">{t('vossusage.helper.noData')}</p>
                )}
            </div>
        </div>
    );
};

export default UsageMetricCard;
